import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dataGridCol } from '@/app/shared/components/Grid/common';
import { CPGrid } from '@/app/shared/components/Grid/CPGrid';
import type { TPhoenixColumnDef } from '@/app/shared/components/Grid/utility/types';
import { downloadAsData, gridRowActionsIconProps } from '@/app/shared/utils/grid';
import { useAppSelector } from '@/store/hooks';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { useFetchStipsQuery, useLazyFetchStipsDetailsQuery } from './stips.services';
import type { DocumentItem, IStip } from './stips.types';

const Stips = () => {
  const [searchParams] = useSearchParams();
  const [fetchStipsDetails] = useLazyFetchStipsDetailsQuery();
  const [documentPreviewModalPosition, setDocumentPreviewModalPosition] = useState(0);
  const [contactStipData, setContactStipData] = useState<DocumentItem>();
  const [stipData, setStipData] = useState<IStip | null>(null);
  const [loadingStipData, setLoadingStipData] = useState(false);
  const reduxContactId = useAppSelector((state) => state.auth.contactId);
  const contactId = searchParams.get('contactId') || reduxContactId || '';
  const { data, isLoading } = useFetchStipsQuery(contactId ?? '', {
    skip: !contactId,
    refetchOnMountOrArgChange: true,
  });
  const downloadFile = useCallback(
    async (row: DocumentItem) => {
      try {
        setLoadingStipData(true);

        const response = await fetchStipsDetails({
          contactStipId: row.contactStipId,
          contactId,
        }).unwrap();

        setStipData(response);

        if (
          response?.content
        ) {
          downloadAsData(
            response.blobFile.name,
            response.content
          );
        }
      } finally {
        setLoadingStipData(false);
      }
    },
    [fetchStipsDetails]
  );

  const onButtonClick = async (row: DocumentItem) => {
    setContactStipData(row);

    const response = await fetchStipsDetails({
      contactStipId: row.contactStipId,
      contactId,
    }).unwrap();

    setStipData(response);

    setDocumentPreviewModalPosition((prev) => prev + 1);
  };

  const columns: TPhoenixColumnDef[] = [
    dataGridCol('fileName', {
      minWidth: 350,
      flex: 2,
      renderCell: ({ value, row }) => (
        <Button
          disabled={row.isDeleted}
          onClick={() => onButtonClick(row)}
          sx={{
            fontSize: {
              xs: '0.6rem',
              sm: '1.1rem',
            },
          }}
        >
          {value}
        </Button>
      ),
    }),
    dataGridCol('actions', {
      minWidth: 120,
      flex: 2,
      headerAlign: 'center',
      align: 'center',
      renderCell: ({ row }) => (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'left' }}>
          <IconButton onClick={() => downloadFile(row)}>
            <FileDownloadIcon sx={gridRowActionsIconProps} />
          </IconButton>
        </div>
      )
    }),
  ];


  const rows =
    data?.documents?.map((doc, index) => ({
      id: doc.contactStipId || index,
      contactStipId: doc.contactStipId,
      fileName: doc.fileName,
      actions: '',
    })) ?? [];

  return (
    <>
      <Box sx={{ height: 'auto', width: '100%', my: 2 }}>
        <Typography fontWeight={700} fontSize={{ xs: 20, sm: 24 }} color="#1a1a1a" mb={1}>
          View Documents
        </Typography>
        <Typography fontSize={14} color="#1a1a1a" lineHeight={1.7} mb={{ xs: 3, sm: 4 }}>
          Documents previously submitted to your Hello Refi Hub.
        </Typography>
        <CPGrid
          name="stipsGrid"
          canNotCopy
          canNotEdit
          autoHeight
          columns={columns}
          loading={isLoading}
          pageSize={10}
          paginationMode="client"
          filterMode="client"
          getRowId={(row) => row.contactStipId}
          rows={isLoading ? [] : [...rows]}
        />
      </Box>
      {contactStipData && !loadingStipData && stipData && (
        <DocumentPreviewModal
          documentPreviewModalPosition={documentPreviewModalPosition}
          open={!!documentPreviewModalPosition}
          contactStipData={contactStipData}
          stipData={stipData}
          onClose={() => {
            setDocumentPreviewModalPosition(0);
            setStipData(null);
          }}
        />
      )}
    </>
  );
};

export default Stips;